import React, { useEffect, useRef, useState } from "react";
import { Animated, Easing, View, Text, StyleSheet } from "react-native";
import { colors } from "@/lib/theme";

interface AnimatedSplashProps {
  onReady?: () => void;
  onFinish: () => void;
  /** When true, the splash fades out. Until then it holds after the animation. */
  ready?: boolean;
}

export default function AnimatedSplash({ onReady, onFinish, ready }: AnimatedSplashProps) {
  // Ball
  const ballX = useRef(new Animated.Value(-180)).current;
  const ballY = useRef(new Animated.Value(250)).current;
  const ballScale = useRef(new Animated.Value(4)).current;
  const ballOpacity = useRef(new Animated.Value(1)).current;

  // Stumps tilt
  const stump1Rotate = useRef(new Animated.Value(0)).current;
  const stump2Rotate = useRef(new Animated.Value(0)).current;
  const stump3Rotate = useRef(new Animated.Value(0)).current;

  // Bails fly off
  const bail1Y = useRef(new Animated.Value(0)).current;
  const bail1X = useRef(new Animated.Value(0)).current;
  const bail1Rotate = useRef(new Animated.Value(0)).current;
  const bail2Y = useRef(new Animated.Value(0)).current;
  const bail2X = useRef(new Animated.Value(0)).current;
  const bail2Rotate = useRef(new Animated.Value(0)).current;
  const bailsOpacity = useRef(new Animated.Value(1)).current;

  // Text
  const textOpacity = useRef(new Animated.Value(0)).current;
  const textY = useRef(new Animated.Value(20)).current;

  // Loading dots pulse
  const dotsOpacity = useRef(new Animated.Value(0)).current;

  // Screen fade
  const screenOpacity = useRef(new Animated.Value(1)).current;

  const [animDone, setAnimDone] = useState(false);

  // Step 1: Run the stumps animation
  useEffect(() => {
    const HIT_TIME = 600;

    // Ball flies to stumps
    Animated.parallel([
      Animated.timing(ballX, { toValue: 0, duration: HIT_TIME, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      Animated.timing(ballY, { toValue: 0, duration: HIT_TIME, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      Animated.timing(ballScale, { toValue: 0.8, duration: HIT_TIME, easing: Easing.out(Easing.quad), useNativeDriver: true }),
    ]).start();

    // Ball disappears + stumps fall
    setTimeout(() => {
      Animated.timing(ballOpacity, { toValue: 0, duration: 80, useNativeDriver: true }).start();
      Animated.parallel([
        Animated.timing(stump1Rotate, { toValue: -30, duration: 400, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(stump2Rotate, { toValue: 20, duration: 450, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(stump3Rotate, { toValue: 45, duration: 380, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(bail1Y, { toValue: -120, duration: 450, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(bail1X, { toValue: -60, duration: 450, useNativeDriver: true }),
        Animated.timing(bail1Rotate, { toValue: 200, duration: 450, useNativeDriver: true }),
        Animated.timing(bail2Y, { toValue: -100, duration: 400, easing: Easing.out(Easing.quad), useNativeDriver: true }),
        Animated.timing(bail2X, { toValue: 70, duration: 400, useNativeDriver: true }),
        Animated.timing(bail2Rotate, { toValue: -180, duration: 400, useNativeDriver: true }),
        Animated.timing(bailsOpacity, { toValue: 0, duration: 400, delay: 200, useNativeDriver: true }),
      ]).start();
    }, HIT_TIME);

    // Text appears
    setTimeout(() => {
      Animated.parallel([
        Animated.timing(textOpacity, { toValue: 1, duration: 300, useNativeDriver: true }),
        Animated.timing(textY, { toValue: 0, duration: 300, easing: Easing.out(Easing.quad), useNativeDriver: true }),
      ]).start();
    }, HIT_TIME + 500);

    // Mark animation done — now we wait for `ready`
    setTimeout(() => {
      setAnimDone(true);
    }, HIT_TIME + 1200);
  }, []);

  // Step 2: Once animation is done and not yet ready, show pulsing dots
  useEffect(() => {
    if (!animDone || ready) return;
    Animated.timing(dotsOpacity, { toValue: 1, duration: 300, useNativeDriver: true }).start();
    const pulse = Animated.loop(
      Animated.sequence([
        Animated.timing(dotsOpacity, { toValue: 0.3, duration: 800, useNativeDriver: true }),
        Animated.timing(dotsOpacity, { toValue: 1, duration: 800, useNativeDriver: true }),
      ])
    );
    pulse.start();
    return () => pulse.stop();
  }, [animDone, ready]);

  // Step 3: Once animation is done AND ready, fade out
  useEffect(() => {
    if (!animDone || !ready) return;
    // Small delay so the final screen has rendered underneath
    const timer = setTimeout(() => {
      Animated.timing(screenOpacity, {
        toValue: 0,
        duration: 400,
        useNativeDriver: true,
      }).start(() => onFinish());
    }, 200);
    return () => clearTimeout(timer);
  }, [animDone, ready]);

  const deg = (v: Animated.Value) =>
    v.interpolate({ inputRange: [-360, 360], outputRange: ["-360deg", "360deg"] });

  return (
    <Animated.View style={[s.container, { opacity: screenOpacity }]} onLayout={() => onReady?.()}>
      {/* Stumps */}
      <View style={s.stumpsArea}>
        <Animated.View style={[s.bail, { left: 0, opacity: bailsOpacity, transform: [{ translateX: bail1X }, { translateY: bail1Y }, { rotate: deg(bail1Rotate) }] }]} />
        <Animated.View style={[s.bail, { left: STUMP_GAP + STUMP_W / 2, opacity: bailsOpacity, transform: [{ translateX: bail2X }, { translateY: bail2Y }, { rotate: deg(bail2Rotate) }] }]} />
        <Animated.View style={[s.stump, { left: 0, transformOrigin: "bottom center", transform: [{ rotate: deg(stump1Rotate) }] }]} />
        <Animated.View style={[s.stump, { left: STUMP_GAP + STUMP_W / 2, transformOrigin: "bottom center", transform: [{ rotate: deg(stump2Rotate) }] }]} />
        <Animated.View style={[s.stump, { left: (STUMP_GAP + STUMP_W / 2) * 2, transformOrigin: "bottom center", transform: [{ rotate: deg(stump3Rotate) }] }]} />
      </View>

      {/* Ball */}
      <Animated.View style={[s.ballWrap, { opacity: ballOpacity, transform: [{ translateX: ballX }, { translateY: ballY }, { scale: ballScale }] }]}>
        <View style={s.ball}>
          <View style={s.ballSeam} />
        </View>
      </Animated.View>

      {/* Text */}
      <Animated.View style={[s.textWrap, { opacity: textOpacity, transform: [{ translateY: textY }] }]}>
        <Text style={s.appName}>inthecrease</Text>
      </Animated.View>

      {/* Loading dots — shown after animation while waiting for data */}
      {animDone && !ready && (
        <Animated.View style={[s.dotsWrap, { opacity: dotsOpacity }]}>
          <View style={s.dotsRow}>
            <View style={s.dot} />
            <View style={s.dot} />
            <View style={s.dot} />
          </View>
        </Animated.View>
      )}
    </Animated.View>
  );
}

const STUMP_W = 8;
const STUMP_H = 70;
const STUMP_GAP = 18;
const STUMPS_TOTAL_W = (STUMP_GAP + STUMP_W / 2) * 2 + STUMP_W;

const s = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.brand,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 999,
  },
  ballWrap: {
    position: "absolute",
  },
  ball: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: "#c0392b",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.3,
    shadowRadius: 4,
    elevation: 4,
  },
  ballSeam: {
    width: 22,
    height: 2.5,
    backgroundColor: "#e8d5b7",
    borderRadius: 1,
    transform: [{ rotate: "20deg" }],
  },
  stumpsArea: {
    width: STUMPS_TOTAL_W,
    height: STUMP_H + 8,
    position: "relative",
  },
  stump: {
    position: "absolute",
    width: STUMP_W,
    height: STUMP_H,
    backgroundColor: "#fff",
    borderRadius: STUMP_W / 2,
    bottom: 0,
  },
  bail: {
    position: "absolute",
    width: STUMP_GAP + STUMP_W,
    height: 6,
    backgroundColor: "rgba(255,255,255,0.88)",
    borderRadius: 3,
    top: 0,
  },
  textWrap: {
    position: "absolute",
    bottom: "35%",
  },
  appName: {
    fontSize: 30,
    fontWeight: "800",
    color: "#fff",
    letterSpacing: -0.5,
  },
  dotsWrap: {
    position: "absolute",
    bottom: "28%",
  },
  dotsRow: {
    flexDirection: "row",
    gap: 8,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "rgba(255,255,255,0.6)",
  },
});
